import { EventEmitter, Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Form } from '@app/shared/form';
import { NbComponentStatus, NbLayoutScrollService } from '@nebular/theme';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export interface ActionFooter {
	label: string;
	class?: string[];
	position: 'left' | 'center' | 'right';
	outline?: boolean;
	disabled?: Observable<boolean>;
	status?: NbComponentStatus;
	click: () => void;
}

@UntilDestroy()
@Injectable()
export class LayoutPageTitleService {
	readonly filterFormName = 'filterForm';
	readonly additionalFilterFormName = 'additionalFilterForm';

	private resetFromComponent = false;

	private _title = new BehaviorSubject<string>('');
	title$ = this._title.asObservable();

	private _filterForm = new BehaviorSubject<{
		containerForm: UntypedFormGroup;
		submitBtnText: string;
		customBtnText: string;
	}>(null);
	filterForm$ = this._filterForm.asObservable();

	private _requiredFieldsText = new BehaviorSubject<boolean>(false);
	requiredFieldsText$ = this._requiredFieldsText.asObservable();

	private _accordionSearchExpanded = new BehaviorSubject<boolean>(false);
	accordionSearchExpanded$ = this._accordionSearchExpanded.asObservable();

	private _accordionAddtionalFilterExpanded = new BehaviorSubject<boolean>(
		false
	);
	accordionAddtionalFilterExpanded$ =
		this._accordionAddtionalFilterExpanded.asObservable();

	search = new EventEmitter<any>();
	customSearch = new EventEmitter<any>();
	reset = new EventEmitter();

	hasFilterForm$ = this.filterForm$.pipe(
		map((f) => !!f)
		/* tap((f) => {
      console.log(f);
    }) */
	);

	constructor(
		private _router: Router,
		private nbScrollService: NbLayoutScrollService
	) {
		this._router.events
			.pipe(
				filter((x) => x instanceof NavigationEnd),
				untilDestroyed(this)
			)
			.subscribe(() => {
				this._resolveTitle(_router.routerState.snapshot.root);
				this._resolveAccordionSearchExpanded(
					_router.routerState.snapshot.root
				);
			});
	}

	private _resolveTitle(route: ActivatedRouteSnapshot) {
		const title = this._getTitle(route);
		if (title) {
			this._title.next(title);
		}
		if (route.firstChild) {
			this._resolveTitle(route.firstChild);
		}
	}

	private _getTitle(route: ActivatedRouteSnapshot) {
		const data = route.routeConfig && route.routeConfig.data;
		return data?.pageTitle;
	}

	private _resolveAccordionSearchExpanded(route: ActivatedRouteSnapshot) {
		const accordionSearchExpanded = this._getAccordionSearchExpanded(route);
		if (accordionSearchExpanded) {
			this._accordionSearchExpanded.next(accordionSearchExpanded);
		}
		if (route.firstChild) {
			this._resolveAccordionSearchExpanded(route.firstChild);
		}
	}

	private _getAccordionSearchExpanded(route: ActivatedRouteSnapshot) {
		const data = route.routeConfig && route.routeConfig.data;
		return data?.accordionSearchExpanded;
	}

	/**
	 *
	 * @param form
	 * @param submitBtnText
	 * @param customBtnText
	 * @param resetFromComponent
	 */
	setFilterForm(
		form: Form,
		submitBtnText = 'UTILS.SEARCH',
		customBtnText = '',
		resetFromComponent = false
	) {
		this.resetFromComponent = resetFromComponent;
		this._filterForm.next({
			containerForm: new UntypedFormGroup({
				[this.filterFormName]: form
			}),
			submitBtnText,
			customBtnText
		});
		this.searchForRequiredTrue(form);
	}

	setAdditionalFilterForm(form: Form) {
		const containerForm = this._filterForm.value.containerForm;
		containerForm.addControl(this.additionalFilterFormName, form);

		this._filterForm.next({ ...this._filterForm.value, containerForm });
	}

	toggleAdditionalFilterForm() {
		this._accordionAddtionalFilterExpanded.next(
			!this._accordionAddtionalFilterExpanded.value
		);
	}

	private searchForRequiredTrue(form) {
		// eslint-disable-next-line guard-for-in
		for (const field in form.controls) {
			const control = form.get(field);
			if (control.required) {
				this._requiredFieldsText.next(true);
				break;
			}
		}
	}

	setRequiredFieldsText() {
		this._requiredFieldsText.next(true);
	}

	searchEmit(isCustom = false) {
		const values = {
			...this._filterForm.value.containerForm?.getRawValue()[
				this.additionalFilterFormName
			],
			...this._filterForm.value.containerForm?.getRawValue()[
				this.filterFormName
			]
		};
		if (isCustom) {
			this.customSearch.emit(values);
		} else {
			this.search.emit(values);
		}

		// scroll alla prima nb-card contenente (in genere) la tabella
		const el: any = document.getElementsByTagName('nb-card');
		if (el.length !== 0) {
			let top;
			el.lenght !== 0 ? (top = el[0].offsetTop - 60) : (top = 200);
			this.nbScrollService.scrollTo(0, top);
		}
	}

	resetEmit() {
		if (this.resetFromComponent) {
			this.reset.emit();
		} else {
			this.reset.emit(this._filterForm.value.containerForm.reset());
		}
	}

	destroy(): void {
		this._title.next('');
		this._accordionSearchExpanded.next(false);
		this._filterForm.next(null);
		this._accordionAddtionalFilterExpanded.next(false);
		this.resetFromComponent = false;
		this._requiredFieldsText.next(false);
	}
}
